const OAUTH_CONFIG = {
  microsoft: {
    authorizeUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    scope: 'openid email profile User.Read',
  },
  google: {
    authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    scope: 'openid email profile',
  },
} as const;

export type OAuthProvider = keyof typeof OAUTH_CONFIG;

interface OAuthStatePayload {
  nonce: string;
  inviteToken?: string;
  returnTo?: string;
}

export function getPreviewRedirectOrigin(): string | null {
  const redirectOrigin = import.meta.env.VITE_PR_OAUTH_REDIRECT_ORIGIN;
  if (!redirectOrigin) {
    return null;
  }

  const normalized = redirectOrigin.trim();
  return normalized.length > 0 ? normalized.replace(/\/$/, '') : null;
}

export function buildOAuthRedirectUri(redirectOrigin: string, provider: OAuthProvider): string {
  return `${redirectOrigin.trim().replace(/\/+$/, '')}/auth/callback/${provider}`;
}

export function buildOAuthUrlWithRedirectOrigin(
  provider: OAuthProvider,
  state: string,
  redirectOrigin: string
): string {
  const config = OAUTH_CONFIG[provider];
  if (!config) {
    throw new Error(`Unsupported OAuth provider: ${provider}`);
  }

  const clientIdKey = `VITE_OAUTH_${provider.toUpperCase()}_CLIENT_ID`;
  const clientId = import.meta.env[clientIdKey];

  if (!clientId) {
    throw new Error(`OAuth configuration error: ${clientIdKey} environment variable is not set.`);
  }

  const redirectUri = buildOAuthRedirectUri(redirectOrigin, provider);

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: config.scope,
    state: state,
  });

  return `${config.authorizeUrl}?${params.toString()}`;
}

export function buildOAuthUrl(provider: OAuthProvider, state: string): string {
  return buildOAuthUrlWithRedirectOrigin(provider, state, window.location.origin);
}

function buildPreviewStartUrl(
  provider: OAuthProvider,
  returnTo: string,
  inviteToken?: string
): string {
  const redirectOrigin = getPreviewRedirectOrigin();
  if (!redirectOrigin) {
    throw new Error('Preview OAuth redirect origin is not configured');
  }

  const params = new URLSearchParams({
    provider,
    returnTo,
  });

  if (inviteToken) {
    params.set('inviteToken', inviteToken);
  }

  return `${redirectOrigin}/auth/start?${params.toString()}`;
}

export function redirectToOAuth(provider: OAuthProvider, inviteToken?: string): void {
  const nonce = crypto.randomUUID();

  const previewRedirectOrigin = getPreviewRedirectOrigin();
  if (previewRedirectOrigin && window.location.origin !== previewRedirectOrigin) {
    const previewStartUrl = buildPreviewStartUrl(provider, window.location.origin, inviteToken);
    const popup = window.open(previewStartUrl, 'm360web-oauth-preview', 'width=500,height=700');
    if (popup) {
      popup.focus();
      return;
    }
    window.location.href = previewStartUrl;
    return;
  }

  const payload: OAuthStatePayload = { nonce };
  if (inviteToken) {
    payload.inviteToken = inviteToken;
  }

  const state = btoa(JSON.stringify(payload));
  sessionStorage.setItem('oauth_nonce', nonce);
  window.location.href = buildOAuthUrlWithRedirectOrigin(
    provider,
    state,
    previewRedirectOrigin ?? window.location.origin
  );
}
